import { useState } from "react";
import { Button } from "@mui/material";

// Enums for shot tracking
enum CallType {
    Draw = "Draw",
    Hit = "Hit"
}

enum TurnType {
    InTurn = "In Turn",
    OutTurn = "Out Turn"
}

enum BroomResult {
    Narrow = "Narrow",
    Wide = "Wide",
    OnBroom = "On Broom"
}

enum WeightResult {
    Light = "Light",
    Heavy = "Heavy",
    Made = "Made"
}

enum ShotResult {
    Made = "Made",
    Missed = "Missed"
}

interface ShotData {
    date: string;
    skip: string;
    call: CallType | "";
    turn: TurnType | "";
    broomResult: BroomResult | "";
    weightResult: WeightResult | "";
    result: ShotResult | "";
}

export function SkipsTracker() {
    // List of skips in alphabetical order
    const skipsList = [
        "Apel",
        "Binish",
        "Blizzard",
        "Boggio",
        "Delsman",
        "Geiger",
        "Goelzer",
        "Kania",
        "Lindsey",
        "MacGillis",
        "McLellan",
        "Packard"
    ];

    // Function to get the previous Monday's date
    const getPreviousMonday = (): string => {
        const today = new Date();
        const day = today.getDay();
        const diff = day === 0 ? -6 : 1 - day; // If Sunday (0), go back 6 days, otherwise go to Monday
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);

        // If today is Monday, return today, otherwise return last Monday
        if (day === 1) {
            return today.toISOString().split('T')[0];
        } else {
            return monday.toISOString().split('T')[0];
        }
    };

    const [shotData, setShotData] = useState<ShotData>({
        date: getPreviousMonday(),
        skip: "",
        call: "",
        turn: "",
        broomResult: "",
        weightResult: "",
        result: ""
    });

    const [saveSuccessful, setSaveSuccessful] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string>("");

    const handleSkipChange = (skip: string) => {
        setShotData({ ...shotData, skip });
    };

    const handleCallChange = (call: CallType) => {
        setShotData({ ...shotData, call, weightResult: "" });
    };

    const handleTurnChange = (turn: TurnType) => {
        setShotData({ ...shotData, turn });
    };

    const handleBroomResultChange = (broomResult: BroomResult) => {
        setShotData({ ...shotData, broomResult });
    };

    const handleWeightResultChange = (weightResult: WeightResult) => {
        setShotData({ ...shotData, weightResult });
    };

    const handleResultChange = (result: ShotResult) => {
        setShotData({ ...shotData, result });
    };

    const isSaveDisabled = () => {
        return (
            shotData.skip === "" ||
            shotData.call === "" ||
            shotData.turn === "" ||
            shotData.broomResult === "" ||
            shotData.result === "" ||
            (shotData.call === CallType.Draw && shotData.weightResult === "")
        );
    };

    const saveShot = async () => {
        setSaveError("");

        try {
            // Prepare data for API
            const dataToSave = {
                date: shotData.date,
                skip: shotData.skip,
                call: shotData.call,
                turn: shotData.turn,
                broomResult: shotData.broomResult,
                weightResult: shotData.call === CallType.Hit ? "" : shotData.weightResult,
                result: shotData.result
            };

            // Call API endpoint (from CurlingDataStorage repo)
            // TODO: Replace with actual API endpoint when CurlingDataStorage repo is deployed
            await fetch('https://67m7uq3175.execute-api.us-east-1.amazonaws.com/Prod/api/Skips', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSave)
            });

            setSaveSuccessful(true);

            // Reset form except date and skip
            setShotData({
                date: shotData.date,
                skip: shotData.skip,
                call: "",
                turn: "",
                broomResult: "",
                weightResult: "",
                result: ""
            });

            // Hide success message after 3 seconds
            setTimeout(() => {
                setSaveSuccessful(false);
            }, 3000);

        } catch (error) {
            console.error('Error saving shot:', error);
            setSaveError("Failed to save shot. Please try again.");
        }
    };

    return (
        <div style={{ padding: "20px" }}>
            <h1>Skips Shot Tracker</h1>

            {saveSuccessful && (
                <div style={{ 
                    padding: "10px", 
                    marginBottom: "20px", 
                    backgroundColor: "#4caf50", 
                    color: "white", 
                    borderRadius: "4px" 
                }}>
                    Shot saved successfully!
                </div>
            )}

            {saveError && (
                <div style={{ 
                    padding: "10px", 
                    marginBottom: "20px", 
                    backgroundColor: "#f44336", 
                    color: "white", 
                    borderRadius: "4px" 
                }}>
                    {saveError}
                </div>
            )}

            <div style={{ marginBottom: "20px" }}>
                <h3>Date:</h3>
                <input
                    type="date"
                    value={shotData.date}
                    onChange={(e) => setShotData({ ...shotData, date: e.target.value })}
                    style={{ padding: "8px", fontSize: "16px" }}
                />
            </div>

            <h3>Skip:</h3>
            <div style={{ marginBottom: "20px" }}>
                {skipsList.map(skip => (
                    <Button
                        key={skip}
                        style={{ marginRight: "10px", marginBottom: "10px" }}
                        variant={shotData.skip === skip ? "contained" : "outlined"}
                        onClick={() => handleSkipChange(skip)}
                    >
                        {skip}
                    </Button>
                ))}
            </div>

            <h3>Call:</h3>
            <div style={{ marginBottom: "20px" }}>
                <Button
                    style={{ marginRight: "10px" }}
                    variant={shotData.call === CallType.Draw ? "contained" : "outlined"}
                    onClick={() => handleCallChange(CallType.Draw)}
                >
                    Draw
                </Button>
                <Button
                    variant={shotData.call === CallType.Hit ? "contained" : "outlined"}
                    onClick={() => handleCallChange(CallType.Hit)}
                >
                    Hit
                </Button>
            </div>

            <h3>Turn:</h3>
            <div style={{ marginBottom: "20px" }}>
                <Button
                    style={{ marginRight: "10px" }}
                    variant={shotData.turn === TurnType.InTurn ? "contained" : "outlined"}
                    onClick={() => handleTurnChange(TurnType.InTurn)}
                >
                    In Turn
                </Button>
                <Button
                    variant={shotData.turn === TurnType.OutTurn ? "contained" : "outlined"}
                    onClick={() => handleTurnChange(TurnType.OutTurn)}
                >
                    Out Turn
                </Button>
            </div>

            <h3>Broom Result:</h3>
            <div style={{ marginBottom: "20px" }}>
                <Button
                    style={{ marginRight: "10px" }}
                    variant={shotData.broomResult === BroomResult.Narrow ? "contained" : "outlined"}
                    onClick={() => handleBroomResultChange(BroomResult.Narrow)}
                >
                    Narrow
                </Button>
                <Button
                    style={{ marginRight: "10px" }}
                    variant={shotData.broomResult === BroomResult.Wide ? "contained" : "outlined"}
                    onClick={() => handleBroomResultChange(BroomResult.Wide)}
                >
                    Wide
                </Button>
                <Button
                    variant={shotData.broomResult === BroomResult.OnBroom ? "contained" : "outlined"}
                    onClick={() => handleBroomResultChange(BroomResult.OnBroom)}
                >
                    On Broom
                </Button>
            </div>

            {shotData.call === CallType.Draw && (
                <>
                    <h3>Weight Result:</h3>
                    <div style={{ marginBottom: "20px" }}>
                        <Button
                            style={{ marginRight: "10px" }}
                            variant={shotData.weightResult === WeightResult.Light ? "contained" : "outlined"}
                            onClick={() => handleWeightResultChange(WeightResult.Light)}
                        >
                            Light
                        </Button>
                        <Button
                            style={{ marginRight: "10px" }}
                            variant={shotData.weightResult === WeightResult.Heavy ? "contained" : "outlined"}
                            onClick={() => handleWeightResultChange(WeightResult.Heavy)}
                        >
                            Heavy
                        </Button>
                        <Button
                            variant={shotData.weightResult === WeightResult.Made ? "contained" : "outlined"}
                            onClick={() => handleWeightResultChange(WeightResult.Made)}
                        >
                            Made
                        </Button>
                    </div>
                </>
            )}

            <h3>Result:</h3>
            <div style={{ marginBottom: "20px" }}>
                <Button
                    style={{ marginRight: "10px" }}
                    variant={shotData.result === ShotResult.Made ? "contained" : "outlined"}
                    onClick={() => handleResultChange(ShotResult.Made)}
                >
                    Made
                </Button>
                <Button
                    variant={shotData.result === ShotResult.Missed ? "contained" : "outlined"}
                    onClick={() => handleResultChange(ShotResult.Missed)}
                >
                    Missed
                </Button>
            </div>

            <Button
                variant="contained"
                color="primary"
                onClick={saveShot}
                disabled={isSaveDisabled()}
                style={{ marginTop: "20px" }}
            >
                Save Shot
            </Button>
        </div>
    );
}